import { AfterViewInit, Component, Inject, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';

import moment from 'moment-timezone';
import { MenuItem, MessageService } from 'primeng/api';
import { filter, Observable, Subscription, take } from 'rxjs';

import { AbstractDialogComponent } from '../../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';
import { TableColumnModel } from '../../../../Forms/models/table-column.model';
import { TableColumnInputEnum } from '../../../../Forms/enums/table-column-input-type.enum';
import { ButtonTypesEnum } from '../../../../Forms/enums/button-types.enum';
import { FilterService } from '../../../../Filter/services/abstract-filter.service';
import { UsersService } from '../../../../../Core/Users/services/users.service';
import { UserModel } from '../../../../../Core/Users/models/user.model';
import { ActionsEnum } from '../../../../Basic/enums/actions.enum';
import { ItemRow, LeftSidebarManagementService } from '../../../services/left-sidebar-management.service';
import { DropdownOptionModel } from '../../../../Forms/models/dropdown-option.model';
import { ListEntitiesVisibilityEnum } from '../../../../MonitorShared/enum/list-entities-visibility.enum';
import { TagsService } from '../../../../Tags/services/tags.service';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { TagModel } from '../../../../Tags/models/tag.model';
import { ReleaseStatus } from '../../../../../api/wire/enums/release-status.enum';
import { BriefingStatusEnum } from '../../../../MonitorShared/enum/briefing-status.enum';
import { DisplayGroupModel } from '../../../models/display-group.model';
import { GroupedRow } from '../../../../Forms/components/GroupedTable/grouped-table.component';
import { DisplayGroupTypeEnum } from '../../../../../api/displayGroups/enums/display-group-type.enum';
import { PanelPositionEnum } from '../../../../Forms/components/KebabMenu/kebab-menu.component';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';

@Component({
    selector: 'agpr-manage-lhp-list-dialog',
    templateUrl: './manage-lhp-list-dialog.component.html',
    styleUrls: ['./manage-lhp-list-dialog.component.scss'],
    providers: [{
        provide: FilterService,
        useExisting: LeftSidebarManagementService
    }],
    standalone: false
})
export class ManageLhpListDialogComponent extends AbstractDialogComponent implements OnInit, AfterViewInit, OnDestroy {
    readonly id = 'manageLhpListDialog';
    readonly buttonTypes = ButtonTypesEnum;

    @Input() header: string;
    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() noContentMessage: string;

    @ViewChild('actionCell') actionCellTemplate: TemplateRef<any>;
    @ViewChild('tagsCell') tagsCellTemplate: TemplateRef<any>;

    private groupedListItems: DisplayGroupModel[] = [];
    public rowData: GroupedRow[] = [];
    public selectedItems: ItemRow[] = [];
    public selectedGroups: GroupedRow[] = [];
    public showSingleGroupHeader: boolean = true;
    public showUngroupedView: boolean = false;

    public hideSlideOverlay: boolean = false;

    private itemKebabColumn: TableColumnModel = {
        type: TableColumnInputEnum.custom,
        sortable: false,
        width: 30,
        customTemplate: null,
        cellStyleClass: 'agpr-manageLhpList-actionsCell'
    };

    public tableColumnDefs: TableColumnModel[] = [];
    public itemNameColumn: TableColumnModel;

    public quickFilterMinLength = 3;
    public quickFilterPlaceholder: string;

    public bulkActions: MenuItem[] = [];

    public itemActions: MenuItem[] = [
        {
            label: 'Core.button.copy',
            command: (event: any) => {
                this.itemAction(event.data, ActionsEnum.copy);
            }
        },
        {
            label: 'Lhp.actions.move',
            command: (event: any) => {
                this.itemAction(event.data, ActionsEnum.move);
            }
        },
        {
            label: 'delete',
            command: (event: any) => {
                this.itemAction(event.data, ActionsEnum.delete);
            }
        }
    ];

    public groupActions: MenuItem[] = [
        {
            label: 'delete',
            command: (event: any) => {
                this.groupAction(event.data, ActionsEnum.delete);
            }
        }
    ];

    public hideAction: MenuItem = {
        label: 'Lhp.actions.hide',
        command: (event: any) => {
            this.itemAction(event.data, ActionsEnum.hide);
        }
    };

    public showAction: MenuItem = {
        label: 'Lhp.actions.show',
        command: (event: any) => {
            this.itemAction(event.data, ActionsEnum.show);
        }
    };

    public favoriteAction: MenuItem  = {
        label: 'Lhp.actions.favorite',
        command: (event: any) => {
            this.itemAction(event.data, ActionsEnum.favorite);
        }
    };

    public unfavoriteAction: MenuItem = {
        label: 'Lhp.actions.unfavorite',
        command: (event: any) => {
            this.itemAction(event.data, ActionsEnum.unfavorite);
        }
    };

    public readonly favoriteItemIndex = 1;
    public readonly visibilityItemIndex = 3;

    public visibilityOptions: DropdownOptionModel[] = [
        {
            label: 'Monitoring.filter.visibility.all',
            value: ListEntitiesVisibilityEnum.all
        },
        {
            label: 'Monitoring.filter.visibility.visible',
            value: ListEntitiesVisibilityEnum.visible
        },
        {
            label: 'Monitoring.filter.visibility.hidden',
            value: ListEntitiesVisibilityEnum.hidden
        }
    ];
    public selectedVisibilityOption: DropdownOptionModel = this.visibilityOptions[0];

    private subscriptions: Subscription[] = [];

    private forbiddenRenameStatusValues: string[] = [
        BriefingStatusEnum.SENT,
        ReleaseStatus.submitted,
        ReleaseStatus.published,
        ReleaseStatus.retracted,
        ReleaseStatus.failed,
        ReleaseStatus.scheduled,
    ];

    public loading: boolean = true;
    public dateFormat: string;

    readonly panelPosition = PanelPositionEnum;

    constructor(
        public coreDialogService: CoreDialogService,
        private usersService: UsersService,
        @Inject(LeftSidebarManagementService) private sidebarManagementService: LeftSidebarManagementService,
        public tagsService: TagsService,
        public localizationService: LocalizationService,
        public messageService: MessageService,
        public currentUserStateService: CurrentUserStateService
    ) {
        super(coreDialogService, 'ManageLhpList');
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.sidebarManagementService.getGroupedListItems().subscribe(groups => {
                this.groupedListItems = groups;
                this.setData();
            }),
            this.sidebarManagementService.getQuickFilterPlaceholder().subscribe(placeholder => this.quickFilterPlaceholder = placeholder),
            this.sidebarManagementService.getTableColumns().subscribe(
                (cols) => {
                    this.tableColumnDefs = [...cols];
                    this.tableColumnDefs.push(this.itemKebabColumn);

                    this.itemNameColumn = this.tableColumnDefs[0];

                    this.setData();
                }
            ),
            this.sidebarManagementService.getLoading().subscribe(loading => this.loading = loading),
            this.sidebarManagementService.getResetSelection().subscribe(reset => {
                if (reset) {
                    this.selectedGroups = [];
                    this.selectedItems = [];
                    this.updatebulkActions();
                }
            }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                this.dateFormat = prefs.getDateFormatString(true);
                this.setData();
            })
        );
        this.sidebarManagementService.setLhpFilters();
    }

    ngAfterViewInit(): void {
        this.itemKebabColumn.customTemplate = this.actionCellTemplate;
        const autoTagsCol = this.tableColumnDefs.find(col => col.field === 'autoTags');
        if (autoTagsCol) {
            autoTagsCol.customTemplate = this.tagsCellTemplate;
        }
        requestAnimationFrame(() => {
            this.tableColumnDefs = this.tableColumnDefs.slice();
        });
    }

    public toggleUngroupedView(): void {
        this.showUngroupedView = !this.showUngroupedView;
        this.setData();
    }

    private setData(): void {
        if (!this.groupedListItems || !this.dateFormat) {
            return;
        }
        const userIds = [];
        const tagIds = [];

        // Filter out Favorites and Uncategorized if they are empty
        const rowGroups = this.groupedListItems.filter(group =>
            group.listItems.length || (group.type !== DisplayGroupTypeEnum.favorites && group.type !== DisplayGroupTypeEnum.uncategorized)
        );
        // If there is only one group and its Uncategorized, don't show the group header
        this.showSingleGroupHeader = rowGroups.length > 1 || rowGroups[0]?.type !== DisplayGroupTypeEnum.uncategorized;

        const rowData = this.showUngroupedView ? [{
            id: 0,
            name: '',
            rows: [],
            selected: false,
            partiallySelected: false,
            editDisabled: true,
            type: null
        }] : [];

        rowGroups.forEach(displayGroup => {
            let rows = displayGroup.listItems.map(item => {
                const row: ItemRow = {
                    name: item.name,
                    createdDate: moment(item.createdDate).format(this.dateFormat),
                    createdByUserId: item.createdByUserId,
                    createdDateRaw: item.createdDate instanceof Date ? item.createdDate.getTime() : item.createdDate,
                    original: item,
                    visibility: this.localizationService.transform(
                        item.hidden ? 'Common.lhp.manageList.dialog.column.visibility.hidden' : 'Common.lhp.manageList.dialog.column.visibility.visible'),
                    actions: [...this.itemActions],
                    editDisabled: !!item['status'] && this.forbiddenRenameStatusValues.includes(item['status']),
                    loading: false,
                    selected: !!this.selectedItems.find(selectedItem => selectedItem.original.id === item.id),
                    type: item.alertType,
                    deliveryMethod: this.localizationService.transform( 'Monitor.leftSidebar.briefings.type.' + item.deliveryMethod),
                };
                userIds.push(item.createdByUserId);

                row.actions.splice(this.favoriteItemIndex, 0, (item.isFavorite ? this.unfavoriteAction : this.favoriteAction));
                row.actions.splice(this.visibilityItemIndex, 0, (item.hidden ? this.showAction : this.hideAction));

                if (item.modifiedByUserId) {
                    row.lastModifiedDate = moment(item.modifiedDate).format(this.dateFormat);
                    row.lastModifiedDateRaw = item.modifiedDate instanceof Date ? item.modifiedDate.getTime() : item.modifiedDate;
                    row.lastModifiedByUserId = item.modifiedByUserId;
                    userIds.push(item.modifiedByUserId);
                }

                return row;
            });

            rows = this.sidebarManagementService.setCustomData(rows);

            if (rows.some(row => row.autoTagIds)) {
                rows.filter(row => row.autoTagIds).forEach(row => tagIds.push(...row.autoTagIds));
            }

            if (this.showUngroupedView) {
                rowData[0].rows.push(...rows);
            } else {
                const group: GroupedRow = {
                    id: displayGroup.id,
                    name: displayGroup.name,
                    rows: rows,
                    selected: !!this.selectedGroups.find(item => item.id === displayGroup.id),
                    partiallySelected: false,
                    editDisabled: displayGroup.type === DisplayGroupTypeEnum.favorites || displayGroup.type === DisplayGroupTypeEnum.uncategorized,
                    type: displayGroup.type
                };

                if (displayGroup.type !== DisplayGroupTypeEnum.favorites && displayGroup.type !== DisplayGroupTypeEnum.uncategorized) {
                    group.actions = this.groupActions;
                }

                rowData.push(group);
            }
        });

        this.rowData = rowData;

        this.getUsers(userIds);

        if (tagIds.length) {
            this.getTags(tagIds, true);
        }
    }

    /**
     * Gets the specified list of users, and updates the row data with their names
     */
    private getUsers(userIds: number[]): void {
        this.usersService.getUsers(userIds).pipe(
            take(1)
        ).subscribe(
            (users: UserModel[]) => {
                const userMap = {};
                users.forEach((user) => {
                    userMap[user.id] = user;
                });
                this.rowData.forEach((group) => {
                    group.rows.forEach(row => {
                        const createdUser = userMap[row.createdByUserId];
                        if (createdUser) {
                            row.createdByUser = createdUser.getDisplayName();
                        }
                        if (row.lastModifiedByUserId) {
                            const modifiedUser = userMap[row.lastModifiedByUserId];
                            if (modifiedUser) {
                                row.lastModifiedByUser = modifiedUser.getDisplayName();
                            }
                        }
                    });
                });
                this.rowData = this.rowData.slice();
            }
        );
    }

    private getTags(tagIds: number[], autoTagging: boolean = false) {
        this.tagsService.getTagsByIds(tagIds).pipe(
            take(1)
        ).subscribe(
            (tags: TagModel[]) => {
                const tagMap = {};
                tags.forEach(tag => {
                    tagMap[tag.id] = tag;
                });
                this.rowData.forEach(group => {
                    group.rows.forEach(row => {
                        if (autoTagging && row.autoTagIds) {
                            row.autoTagIds.forEach(id => {
                                if (tagMap[id]) {
                                    row.autoTags.push({label: tagMap[id].name, id: id});
                                }

                            });
                        } else if (row.tagIds) {
                            row.tagIds.forEach(id => {
                                if (tagMap[id]) {
                                    row.tags.push({label: tagMap[id].name, id: id});
                                }
                            });
                        }
                    });
                });
            }
        );
    }

    /**
     * Checks to see if any rows have been selected
     */
    public checkForSelected(): void {
        this.selectedGroups = [];
        this.selectedItems = [];
        this.rowData.forEach(group => {
            if (group.selected) {
                this.selectedGroups.push(group);
            }
            const selectedItems = group.rows.filter(row => row.selected);
            this.selectedItems.push(...selectedItems);
        });
        this.updatebulkActions();
    }

    /**
     * Called when items are selected or edited in the table.
     * Only need to handle edit.
     * @param item
     */
    public updateItem(item: ItemRow) {
        if (item.name !== item.original.name) {
            item.original.name = item.name;
            this.itemAction(item, ActionsEnum.rename);
        }
    }

    public updateGroup(group: GroupedRow) {
        const original = this.groupedListItems.find(displayGroup => displayGroup.id === group.id);
        if (original.name !== group.name) {
            original.name = group.name;
            this.sidebarManagementService.groupAction(original, ActionsEnum.rename);
        }
    }

    /**
     * Called from items kebab menu
     * @param item
     * @param action
     */
    private itemAction(item: ItemRow, action: ActionsEnum) {
        if (action !== ActionsEnum.copy && action !== ActionsEnum.move) {
            item.loading = true;
        }
        this.sidebarManagementService.handleItemAction(item.original, action);
    }

    /**
     * Called from bulk actions button
     * @param item
     * @param action
     */
    private bulkItemAction(action: ActionsEnum) {
        let selectedItems = this.selectedItems.map(x => x.original);
        if (action === ActionsEnum.hide) {
            selectedItems = selectedItems.filter(item => !item.hidden);
        } else if (action === ActionsEnum.show) {
            selectedItems = selectedItems.filter(item => item.hidden);
        }

        const selectedGroupIds = new Set<number>([
            ...this.selectedGroups.map(item => item.id),
            ...selectedItems.filter(x => x.groupId || x.groupId === 0).map(item => item.groupId)
        ]);
        const selectedGroups = this.groupedListItems.filter(item => {
            return selectedGroupIds.has(item.id) && item.type !== DisplayGroupTypeEnum.favorites && item.type !== DisplayGroupTypeEnum.uncategorized;
        });
        if (selectedGroups.length || selectedItems.length) {
            this.sidebarManagementService.handleBulkItemAction(selectedItems, selectedGroups, action);
        }
    }

    // TODO When grouping available
    public createGroup() {
        this.sidebarManagementService.createGroup();
    }

    public groupAction(groupRow: GroupedRow, action: ActionsEnum) {
        const group = this.groupedListItems.find(x => x.id === groupRow.id);
        this.sidebarManagementService.groupAction(group, action);
    }

    /**
     * Updates visibility filter from dropdown menu
     * @param option
     */
    public setVisibilityOption(option: DropdownOptionModel): void {
        this.sidebarManagementService.setVisibilityFilter(option.value);
    }

    public updatebulkActions(): void {
        this.bulkActions = [
            {
                label: 'Lhp.actions.favorite',
                command: () => {
                    this.bulkItemAction(ActionsEnum.favorite);
                },
                disabled: !(this.selectedItems?.length > 0)
            },
            {
                label: 'Lhp.actions.unfavorite',
                command: () => {
                    this.bulkItemAction(ActionsEnum.unfavorite);
                },
                disabled: !(this.selectedItems?.length > 0)
            },
            {
                label: 'Lhp.actions.move',
                command: () => {
                    this.bulkItemAction(ActionsEnum.move);
                },
                disabled: !(this.selectedItems?.length > 0)
            },
            {
                label: 'Lhp.actions.hide',
                command: () => {
                    this.bulkItemAction(ActionsEnum.hide);
                },
                disabled: !(this.selectedItems?.length > 0)
            },
            {
                label: 'Lhp.actions.show',
                command: () => {
                    this.bulkItemAction(ActionsEnum.show);
                },
                disabled: !(this.selectedItems?.length > 0)
            },
            {
                label: 'delete',
                command: () => {
                    this.bulkItemAction(ActionsEnum.delete);
                }
            }
        ];
    }

    public cancel(): void {
        this.hideSlideOverlay = true;
        this.closeDialog();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

}
